import { discountChangeNeedsApproval, holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { IdMultiSelect, type PickOption } from '../../components/ui/IdMultiSelect.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { type DiscountScope, type DiscountType, discountsApi } from '../../services/discounts.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { ApprovalPanel } from '../approvals/approval.js';
import { CustomerPicker } from './components/CustomerPicker.js';

interface DiscountFormData {
  scope: DiscountScope;
  discount_type: DiscountType;
  discount_value: string;
  max_discount_minor: string;
  valid_from: string;
  valid_until: string;
  // code-only
  name: string;
  description: string;
  min_booking_amount_minor: string;
  total_redemptions_limit: string;
  usage_per_user: string;
  car_ids: string[];
  car_model_ids: string[];
  rental_window_from: string;
  rental_window_to: string;
  non_combinable: boolean;
  code_count: string;
  // customer-only
  customer_id: string;
  note: string;
}

const EMPTY: DiscountFormData = {
  scope: 'code',
  discount_type: 'percent',
  discount_value: '10',
  max_discount_minor: '',
  valid_from: '',
  valid_until: '',
  name: '',
  description: '',
  min_booking_amount_minor: '',
  total_redemptions_limit: '',
  usage_per_user: '',
  car_ids: [],
  car_model_ids: [],
  rental_window_from: '',
  rental_window_to: '',
  non_combinable: false,
  code_count: '10',
  customer_id: '',
  note: '',
};

const dateOnly = (iso?: string) => (iso ? iso.slice(0, 10) : '');

export function DiscountFormPage() {
  const navigate = useNavigate();
  const goBack = useBackNavigation('/discounts');
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState<DiscountFormData>(EMPTY);
  const [customerLabelText, setCustomerLabelText] = useState('');
  const [error, setError] = useState('');

  const { data: existing } = useQuery({
    queryKey: RESOURCE_KEYS.discount.one(id as string),
    queryFn: () => discountsApi.get(id as string),
    enabled: isEdit,
  });
  // 372 (review): an approver who may not edit sees the voucher read-only (the server would refuse the save).
  const ability = useAbility();
  const canEdit = holdsAnywhere(ability as never, 'update', 'Discount');
  const qc = useQueryClient();
  // 372 (#1240): approve, or reject with a reason.
  const decide = useMutation({
    mutationFn: (d: { decision: 'approve' | 'reject'; reason?: string }) =>
      discountsApi.decide(id as string, d.decision, d.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RESOURCE_KEYS.discount.one(id as string) });
      qc.invalidateQueries({ queryKey: RESOURCE_KEYS.discount.list });
    },
  });

  useEffect(() => {
    if (!existing) return;
    // Response shape: `customer` is a { id, label } ref (or null for a deleted
    // ref). The form submits `customer_id` as a plain string in the body.
    const c = existing.customer;
    setForm({
      scope: existing.scope,
      discount_type: existing.discount_type,
      discount_value:
        existing.discount_type === 'fixed_amount' && existing.scope === 'customer'
          ? (existing.discount_value / 100).toFixed(2)
          : String(existing.discount_value),
      max_discount_minor:
        existing.max_discount_minor != null ? String(existing.max_discount_minor) : '',
      valid_from: dateOnly(existing.valid_from),
      valid_until: dateOnly(existing.valid_until),
      name: existing.name ?? '',
      description: existing.description ?? '',
      min_booking_amount_minor:
        existing.min_booking_amount_minor != null ? String(existing.min_booking_amount_minor) : '',
      total_redemptions_limit:
        existing.total_redemptions_limit != null ? String(existing.total_redemptions_limit) : '',
      usage_per_user: existing.usage_per_user != null ? String(existing.usage_per_user) : '',
      car_ids: existing.car_ids ?? [],
      car_model_ids: existing.car_model_ids ?? [],
      rental_window_from: dateOnly(existing.rental_window?.from),
      rental_window_to: dateOnly(existing.rental_window?.to),
      non_combinable: existing.non_combinable ?? false,
      code_count: '10',
      customer_id: c?.id ?? '',
      note: existing.note ?? '',
    });
    if (c != null) setCustomerLabelText(c.label || c.id);
  }, [existing]);

  function set<K extends keyof DiscountFormData>(k: K, v: DiscountFormData[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const isCode = form.scope === 'code';
  const isPercent = form.discount_type === 'percent';

  // Option sources for the car / car-model restriction pickers. Code-scope only
  // (customer discounts carry no car restrictions). Cars are fetched in one
  // capped page and filtered client-side — the search endpoint has no
  // free-text param, so a huge fleet would truncate (surfaced via capNote).
  const CAR_PAGE_LIMIT = 1000;
  const { data: carsResp, isLoading: carsLoading } = useQuery({
    queryKey: ['discount-cars'],
    queryFn: () =>
      api.get<{
        items: { _id: string; name: string; plate_number: string; supplier_id?: string }[];
        total: number;
      }>(`/cars?page=1&limit=${CAR_PAGE_LIMIT}`),
    enabled: isCode,
  });
  // 383 (#1272): "Restrict to Supplier" narrows the car list and offers a select-all of that supplier's cars.
  // Nothing about the supplier is saved: the voucher keeps storing `car_ids`. Offered only to a caller the
  // supplier list itself admits (`GET /suppliers` checks `manage Supplier`), so a voucher-only role sees
  // today's form and never meets a 403.
  const mayListSuppliers = ability.can('manage', 'Supplier');
  const { data: suppliersResp } = useQuery({
    queryKey: ['discount-suppliers'],
    queryFn: () => api.get<{ items: { _id: string; company_name: string }[] }>('/suppliers'),
    enabled: isCode && mayListSuppliers,
  });
  const [carSupplierId, setCarSupplierId] = useState('');
  const { data: models, isLoading: modelsLoading } = useQuery({
    queryKey: ['discount-car-models'],
    queryFn: () => api.get<{ _id: string; name: string }[]>('/car-models?status=active'),
    enabled: isCode,
  });

  const carOptions: PickOption[] = (carsResp?.items ?? []).map((c) => ({
    id: c._id,
    label: `${c.name} (${c.plate_number})`,
  }));
  const supplierCarIds = carSupplierId
    ? (carsResp?.items ?? [])
        .filter((c) => String(c.supplier_id) === carSupplierId)
        .map((c) => c._id)
    : [];
  const shownCarOptions = carSupplierId
    ? carOptions.filter((o) => supplierCarIds.includes(o.id))
    : undefined;
  const carSupplierName =
    suppliersResp?.items.find((s) => s._id === carSupplierId)?.company_name ?? '';
  const modelOptions: PickOption[] = (models ?? []).map((m) => ({ id: m._id, label: m.name }));
  const carCapNote =
    carsResp && carsResp.total > carsResp.items.length
      ? `Showing the first ${carsResp.items.length} of ${carsResp.total} cars — selection is limited to this list.`
      : undefined;

  /**
   * 372 (review): a date whose day the form did not change is sent back as the stored instant, not rewritten to
   * 00:00 / 23:59:59.999. Rewriting it would silently lengthen a voucher made elsewhere (a seed, a migrated one) by
   * up to a day without approval, or mark an unchanged save as an increase.
   */
  const keep = (day: string, stored: string | undefined, fallback: string) =>
    stored && dateOnly(stored) === day ? stored : fallback;

  function buildPayload(): Record<string, unknown> {
    const shared: Record<string, unknown> = {
      scope: form.scope,
      discount_type: form.discount_type,
      max_discount_minor:
        isPercent && form.max_discount_minor !== '' ? Number(form.max_discount_minor) : undefined,
    };
    if (isCode) {
      const carIds = form.car_ids;
      const carModelIds = form.car_model_ids;
      const payload: Record<string, unknown> = {
        ...shared,
        name: form.name,
        description: form.description || undefined,
        discount_value: Number(form.discount_value),
        min_booking_amount_minor:
          form.min_booking_amount_minor !== '' ? Number(form.min_booking_amount_minor) : undefined,
        valid_from: keep(
          form.valid_from,
          existing?.valid_from,
          new Date(`${form.valid_from}T00:00:00.000Z`).toISOString(),
        ),
        valid_until: keep(
          form.valid_until,
          existing?.valid_until,
          new Date(`${form.valid_until}T23:59:59.999Z`).toISOString(),
        ),
        total_redemptions_limit:
          form.total_redemptions_limit !== '' ? Number(form.total_redemptions_limit) : undefined,
        usage_per_user: form.usage_per_user !== '' ? Number(form.usage_per_user) : undefined,
        car_ids: carIds.length ? carIds : undefined,
        car_model_ids: carModelIds.length ? carModelIds : undefined,
        rental_window:
          form.rental_window_from && form.rental_window_to
            ? {
                from: keep(
                  form.rental_window_from,
                  existing?.rental_window?.from,
                  new Date(`${form.rental_window_from}T00:00:00.000Z`).toISOString(),
                ),
                to: keep(
                  form.rental_window_to,
                  existing?.rental_window?.to,
                  new Date(`${form.rental_window_to}T23:59:59.999Z`).toISOString(),
                ),
              }
            : undefined,
        non_combinable: form.non_combinable,
      };
      if (!isEdit) payload.code_count = Number(form.code_count);
      return payload;
    }
    // customer scope: value in RM converted to minor for fixed_amount
    const value = isPercent
      ? Math.round(Number(form.discount_value))
      : Math.round(Number(form.discount_value) * 100);
    const payload: Record<string, unknown> = {
      ...shared,
      discount_value: value,
      valid_from: form.valid_from
        ? keep(
            form.valid_from,
            existing?.valid_from,
            new Date(`${form.valid_from}T00:00:00.000Z`).toISOString(),
          )
        : undefined,
      valid_until: form.valid_until
        ? keep(
            form.valid_until,
            existing?.valid_until,
            new Date(`${form.valid_until}T23:59:59.999Z`).toISOString(),
          )
        : undefined,
      note: form.note.trim() || undefined,
    };
    if (!isEdit) payload.customer_id = form.customer_id;
    return payload;
  }

  // 372: what this edit would give away, from the form as it stands (null when nothing, or the voucher is rejected).
  const increase = (() => {
    if (!isEdit || !existing || existing.approval?.status === 'rejected') return null;
    try {
      const { scope: _scope, code_count: _count, ...patch } = buildPayload();
      const r = discountChangeNeedsApproval(existing as unknown as Record<string, unknown>, patch);
      return r.needed ? r.changes : null;
    } catch {
      return null; // the form is still filling in (an empty date cannot be read yet)
    }
  })();

  // 161 (US2): this form invalidated nothing, so the discounts list kept the old row after a save.
  const { save, isPending } = useSaveMutation({
    resource: 'discount',
    id,
    mutationFn: () => {
      const payload = buildPayload();
      return isEdit ? discountsApi.update(id as string, payload) : discountsApi.create(payload);
    },
    onSaved: (data) => {
      if (!isEdit && isCode && data && 'id' in data) {
        navigate(`/discounts/${(data as { id: string }).id}/codes`);
      } else {
        navigate('/discounts');
      }
    },
    onError: (err) =>
      setError(
        (err as { message?: string }).message ??
          `Failed to ${isEdit ? 'update' : 'create'} discount`,
      ),
  });

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Discount' : 'New Discount'}
        description={
          isEdit
            ? 'Update this discount and its restrictions.'
            : 'Create a code campaign or a per-customer discount.'
        }
        fallback="/discounts"
      />
      {isEdit && existing && (
        <div className="mb-6 max-w-2xl">
          <ApprovalPanel
            approval={existing.approval}
            history={existing.approval_history}
            decision={existing.decision ?? null}
            kind="voucher"
            busy={decide.isPending}
            error={
              decide.error ? ((decide.error as { message?: string }).message ?? 'Failed.') : null
            }
            onApprove={() => decide.mutate({ decision: 'approve' })}
            onReject={(reason) => decide.mutate({ decision: 'reject', reason })}
          />
        </div>
      )}
      <form
        className="max-w-2xl space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          if (isCode) {
            if (!form.name) {
              setError('Campaign name is required.');
              return;
            }
          } else if (!isEdit && !form.customer_id) {
            setError('Select a customer.');
            return;
          }
          void save();
        }}
      >
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Scope</h2>
          <FormField
            label="Discount Scope"
            name="scope"
            required
            hint="Code campaigns generate redemption codes; customer discounts apply to one customer."
          >
            <Select
              id="scope"
              value={form.scope}
              disabled={isEdit}
              onChange={(e) => set('scope', (e.target as HTMLSelectElement).value as DiscountScope)}
            >
              <option value="code">Code campaign</option>
              <option value="customer">Customer discount</option>
            </Select>
          </FormField>
        </Card>

        {isCode ? (
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-ink">Campaign details</h2>
            <div className="space-y-4">
              <FormField label="Campaign Name" name="name" required>
                <Input
                  id="name"
                  value={form.name}
                  onInput={(e) => set('name', (e.target as HTMLInputElement).value)}
                  required
                />
              </FormField>
              <FormField label="Description" name="description">
                <Textarea
                  id="description"
                  rows={2}
                  value={form.description}
                  onInput={(e) => set('description', (e.target as HTMLTextAreaElement).value)}
                />
              </FormField>
            </div>
          </Card>
        ) : (
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-ink">Customer</h2>
            {isEdit ? (
              <FormField label="Customer" name="customer_locked" hint="Customer cannot be changed.">
                <Input
                  id="customer_locked"
                  value={customerLabelText || form.customer_id}
                  disabled
                />
              </FormField>
            ) : (
              <CustomerPicker
                value={form.customer_id}
                label={customerLabelText}
                onSelect={(cid, cl) => {
                  set('customer_id', cid);
                  setCustomerLabelText(cl);
                }}
              />
            )}
          </Card>
        )}

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Discount</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Discount Type" name="discount_type" required>
              <Select
                id="discount_type"
                value={form.discount_type}
                onChange={(e) =>
                  set('discount_type', (e.target as HTMLSelectElement).value as DiscountType)
                }
              >
                <option value="percent">Percent (%)</option>
                <option value="fixed_amount">
                  {isCode ? 'Fixed (minor units)' : 'Fixed (RM)'}
                </option>
              </Select>
            </FormField>
            <FormField
              label="Discount Value"
              name="discount_value"
              required
              hint={
                isPercent
                  ? 'Percentage off, e.g. 10 = 10%'
                  : isCode
                    ? 'Amount off in minor units (cents)'
                    : 'Amount off in RM, e.g. 50.00'
              }
            >
              <Input
                id="discount_value"
                type="number"
                min={0}
                step={isPercent || isCode ? 1 : 0.01}
                value={form.discount_value}
                onInput={(e) => set('discount_value', (e.target as HTMLInputElement).value)}
                required
              />
            </FormField>
            {isCode && (
              <FormField
                label="Min Booking Amount"
                name="min_booking_amount_minor"
                hint="Minor units (cents). Blank = no minimum."
              >
                <Input
                  id="min_booking_amount_minor"
                  type="number"
                  min={0}
                  value={form.min_booking_amount_minor}
                  onInput={(e) =>
                    set('min_booking_amount_minor', (e.target as HTMLInputElement).value)
                  }
                />
              </FormField>
            )}
            {isPercent && (
              <FormField
                label="Max Discount"
                name="max_discount_minor"
                hint="Minor units (cents). Caps a percentage discount."
              >
                <Input
                  id="max_discount_minor"
                  type="number"
                  min={0}
                  value={form.max_discount_minor}
                  onInput={(e) => set('max_discount_minor', (e.target as HTMLInputElement).value)}
                />
              </FormField>
            )}
          </div>
        </Card>

        {isCode && (
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-ink">Restrictions</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="Usage Per Customer"
                name="usage_per_user"
                hint="Max redemptions per customer. Blank = unlimited."
              >
                <Input
                  id="usage_per_user"
                  type="number"
                  min={1}
                  value={form.usage_per_user}
                  onInput={(e) => set('usage_per_user', (e.target as HTMLInputElement).value)}
                />
              </FormField>
              <FormField label="Non-combinable" name="non_combinable">
                <Select
                  id="non_combinable"
                  value={form.non_combinable ? 'yes' : 'no'}
                  onChange={(e) =>
                    set('non_combinable', (e.target as HTMLSelectElement).value === 'yes')
                  }
                >
                  <option value="no">Can combine with other discounts</option>
                  <option value="yes">Cannot combine</option>
                </Select>
              </FormField>
              <div className="flex flex-col gap-4">
                {mayListSuppliers && (
                  <FormField
                    label="Restrict to Supplier"
                    name="car_supplier"
                    hint="Narrows the car list and lets you pick a supplier's cars at once. Cars the supplier adds later are not included."
                  >
                    <Select
                      id="car_supplier"
                      value={carSupplierId}
                      onChange={(e) => setCarSupplierId((e.target as HTMLSelectElement).value)}
                    >
                      <option value="">All suppliers</option>
                      {(suppliersResp?.items ?? []).map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.company_name}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                )}
                <IdMultiSelect
                  label="Restrict to Cars"
                  name="car_ids"
                  hint="Pick cars this code applies to. Leave empty = all cars."
                  options={carOptions}
                  shown={shownCarOptions}
                  selected={form.car_ids}
                  onChange={(ids) => set('car_ids', ids)}
                  loading={carsLoading}
                  capNote={carCapNote}
                  toolbar={
                    carSupplierId && supplierCarIds.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-2 text-xs">
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={() =>
                            set('car_ids', [
                              ...form.car_ids,
                              ...supplierCarIds.filter((cid) => !form.car_ids.includes(cid)),
                            ])
                          }
                        >
                          Select all {supplierCarIds.length} cars of {carSupplierName}
                        </button>
                        <button
                          type="button"
                          className="text-steel hover:underline"
                          onClick={() =>
                            set(
                              'car_ids',
                              form.car_ids.filter((cid) => !supplierCarIds.includes(cid)),
                            )
                          }
                        >
                          Clear {carSupplierName} cars
                        </button>
                      </div>
                    ) : undefined
                  }
                />
              </div>
              <IdMultiSelect
                label="Restrict to Car Models"
                name="car_model_ids"
                hint="Pick car models this code applies to. Leave empty = all models."
                options={modelOptions}
                selected={form.car_model_ids}
                onChange={(ids) => set('car_model_ids', ids)}
                loading={modelsLoading}
              />
              <FormField
                label="Rental Window From"
                name="rental_window_from"
                hint="Rental dates must fall within this window."
              >
                <Input
                  id="rental_window_from"
                  type="date"
                  value={form.rental_window_from}
                  onInput={(e) => set('rental_window_from', (e.target as HTMLInputElement).value)}
                />
              </FormField>
              <FormField label="Rental Window To" name="rental_window_to">
                <Input
                  id="rental_window_to"
                  type="date"
                  value={form.rental_window_to}
                  onInput={(e) => set('rental_window_to', (e.target as HTMLInputElement).value)}
                />
              </FormField>
            </div>
          </Card>
        )}

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">
            {isCode ? 'Validity & codes' : 'Validity & note'}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Valid From"
              name="valid_from"
              required={isCode}
              hint={isCode ? undefined : 'Optional window start.'}
            >
              <Input
                id="valid_from"
                type="date"
                value={form.valid_from}
                onInput={(e) => set('valid_from', (e.target as HTMLInputElement).value)}
                required={isCode}
              />
            </FormField>
            <FormField
              label="Valid Until"
              name="valid_until"
              required={isCode}
              hint={isCode ? undefined : 'Optional window end.'}
            >
              <Input
                id="valid_until"
                type="date"
                value={form.valid_until}
                onInput={(e) => set('valid_until', (e.target as HTMLInputElement).value)}
                required={isCode}
              />
            </FormField>
            {isCode && (
              <FormField
                label="Total Redemptions Limit"
                name="total_redemptions_limit"
                hint="Across all codes. Blank = unlimited."
              >
                <Input
                  id="total_redemptions_limit"
                  type="number"
                  min={1}
                  value={form.total_redemptions_limit}
                  onInput={(e) =>
                    set('total_redemptions_limit', (e.target as HTMLInputElement).value)
                  }
                />
              </FormField>
            )}
            {isCode && !isEdit && (
              <FormField
                label="Number of Codes to Generate"
                name="code_count"
                required
                hint="1–10,000 unique codes."
              >
                <Input
                  id="code_count"
                  type="number"
                  min={1}
                  max={10000}
                  value={form.code_count}
                  onInput={(e) => set('code_count', (e.target as HTMLInputElement).value)}
                  required
                />
              </FormField>
            )}
            {!isCode && (
              <FormField
                label="Internal note"
                name="note"
                hint="Internal note, e.g. reason for the discount."
              >
                <Textarea
                  id="note"
                  rows={2}
                  value={form.note}
                  onInput={(e) => set('note', (e.target as HTMLTextAreaElement).value)}
                />
              </FormField>
            )}
          </div>
        </Card>

        {/* 372 (#1240): an edit that gives more away sends the voucher back for approval. The same rule as the server. */}
        {increase && (
          <p
            data-testid="approval-increase-warning"
            className="border-l-4 border-yellow-400 bg-yellow-50 px-3 py-2 text-sm text-ink dark:bg-yellow-500/10"
          >
            <strong>This change gives more away</strong> ({increase.join(', ')}). Saving sends it
            back for approval; it cannot be used until another administrator approves it. Lowering a
            value or switching it off applies at once.
          </p>
        )}
        {!isEdit && (
          <p className="text-sm text-steel">
            A new voucher cannot be used until an administrator who did not create it approves it.
          </p>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3">
          <Button type="submit" loading={isPending} disabled={isEdit && !canEdit}>
            {isEdit
              ? isPending
                ? 'Saving…'
                : increase
                  ? 'Save and send for approval'
                  : 'Save Changes'
              : isPending
                ? 'Creating…'
                : 'Create'}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
